import React from "react";
import { Link } from "react-router-dom";
import { LucideIcon } from "lucide-react";
import { enhancedPageLayoutStyles } from "../ui/enhanced-page-layout";

/**
 * Sidebar section component for consistent styling
 */
export interface SidebarSectionProps {
  children: React.ReactNode;
  title?: string;
  isFirst?: boolean;
  className?: string;
  hideOnMobile?: boolean;
}

export function SidebarSection({
  children,
  title,
  isFirst = false,
  className = "",
  hideOnMobile = false,
}: SidebarSectionProps) {
  return (
    <div
      className={`p-6 ${
        isFirst ? "pt-8" : ""
      } ${hideOnMobile ? "hidden lg:block" : ""} border-b border-border ${className}`}
    >
      {title && (
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
      )}
      {children}
    </div>
  );
}

/**
 * Action buttons container for sidebar
 */
export interface ActionButtonsProps {
  children: React.ReactNode;
  className?: string;
}

export function ActionButtons({
  children,
  className = "",
}: ActionButtonsProps) {
  return <div className={`flex flex-col gap-3 ${className}`}>{children}</div>;
}

/**
 * Reusable primary action button for sidebar
 */
export interface ActionButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  disabled?: boolean;
  icon?: LucideIcon;
  className?: string;
  form?: string;
}

export function ActionButton({
  children,
  onClick,
  type = "button",
  disabled = false,
  icon: Icon,
  className = "",
  form,
}: ActionButtonProps) {
  const baseClass = disabled
    ? enhancedPageLayoutStyles.button.actionPrimaryDisabled
    : enhancedPageLayoutStyles.button.actionPrimary;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      form={form}
      className={`${baseClass} ${className}`}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
}

/**
 * Reusable danger action button for sidebar (e.g., delete)
 */
export interface ActionButtonDangerProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  icon?: LucideIcon;
  className?: string;
}

export function ActionButtonDanger({
  children,
  onClick,
  disabled = false,
  icon: Icon,
  className = "",
}: ActionButtonDangerProps) {
  const baseClass = disabled
    ? enhancedPageLayoutStyles.button.actionDangerDisabled
    : enhancedPageLayoutStyles.button.actionDanger;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${baseClass} ${className}`}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
}

/**
 * Reusable secondary action button for sidebar (e.g., cancel)
 */
export interface ActionButtonSecondaryProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

export function ActionButtonSecondary({
  children,
  onClick,
  disabled = false,
  className = "",
}: ActionButtonSecondaryProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${enhancedPageLayoutStyles.button.actionSecondary} ${className}`}
    >
      {children}
    </button>
  );
}

/**
 * Reusable action button link for sidebar (e.g., edit link)
 */
export interface ActionButtonLinkProps {
  children: React.ReactNode;
  to: string;
  icon?: LucideIcon;
  className?: string;
}

export function ActionButtonLink({
  children,
  to,
  icon: Icon,
  className = "",
}: ActionButtonLinkProps) {
  return (
    <Link
      to={to}
      className={`${enhancedPageLayoutStyles.button.actionPrimary} ${className}`}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </Link>
  );
}

/**
 * Detail field component for sidebar details section
 */
export interface DetailFieldProps {
  label: string;
  value: React.ReactNode;
  className?: string;
}

export function DetailField({
  label,
  value,
  className = "",
}: DetailFieldProps) {
  return (
    <div className={className}>
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className="text-gray-900 font-medium">{value ?? "N/A"}</p>
    </div>
  );
}
